import React from "react";
import { useScrollReveal } from "../hooks/useScrollReveal";
import { Star, ArrowRight } from "lucide-react";

const MAKHANA_IMG =
  "https://images.pexels.com/photos/7051132/pexels-photo-7051132.jpeg?auto=compress&cs=tinysrgb&dpr=2&h=650&w=940";

const products = [
  {
    id: 1,
    name: "4 Suta Grade",
    hindi: "4 सूता मखाना",
    badge: "Heritage Choice",
    badgeClass: "text-blue-400 border-blue-400/30 bg-blue-400/10",
    size: "12.68mm diameter",
    priceRange: "Starting ₹400/kg",
    stars: 4,
    uses: ["Daily Cooking", "Kheer & Sweets", "Budget Snacking"],
    desc:
      "Medium-sized, light, and perfectly crispy makhana. Ideal for everyday Indian cooking, sweets (kheer, raita), and budget-conscious bulk buyers.",
    tag: null,
  },
  {
    id: 2,
    name: "5 Suta Grade",
    hindi: "5 सूता मखाना",
    badge: "Premium Choice",
    badgeClass: "text-[#D4AF37] border-[#D4AF37]/30 bg-[#D4AF37]/10",
    size: "15.85mm diameter",
    priceRange: "Starting ₹500/kg",
    stars: 5,
    uses: ["Retail Packaging", "Health Snacking", "Gifting"],
    desc:
      "Large, beautifully round, and exceptionally crunchy. The most loved grade for health brands, retail packaging, and gifting purposes.",
    tag: "Best Seller",
    tagClass: "bg-[#D4AF37] text-black",
  },
  {
    id: 3,
    name: "6 Suta Grade",
    hindi: "6 सूता मखाना",
    badge: "Royal Choice",
    badgeClass: "text-amber-400 border-amber-400/30 bg-amber-400/10",
    size: "19.02mm diameter",
    priceRange: "Starting ₹650/kg",
    stars: 5,
    uses: ["Export Markets", "Luxury Retail", "Corporate Gifting"],
    desc:
      "The crown jewel of makhana. Extra-large, rare, and supremely crispy — this export-grade luxury makhana is reserved for premium and international markets.",
    tag: "Luxury",
    tagClass: "bg-amber-500/20 text-amber-400 border border-amber-500/40",
  },
  {
    id: 4,
    name: "Mix Grade",
    hindi: "मिक्स मखाना",
    badge: "Smart Choice",
    badgeClass: "text-green-400 border-green-400/30 bg-green-400/10",
    size: "Multiple sizes",
    priceRange: "Starting ₹380/kg",
    stars: 4,
    uses: ["Bulk Buying", "Mass Production", "Value Snacking"],
    desc:
      "An economical blend of multiple grades offering the best value for bulk buyers, mass-market brands, and diverse culinary requirements.",
    tag: "Best Value",
    tagClass: "bg-green-500/20 text-green-400 border border-green-500/40",
  },
];

const ProductsSection = () => {
  const { ref, isVisible } = useScrollReveal();

  const scrollToDistributor = () => {
    document.querySelector("#distributor")?.scrollIntoView({ behavior: "smooth" });
  };

  return (
    <section id="products" ref={ref} className="py-24 bg-[#050505]" data-testid="products-section">
      <div className="max-w-7xl mx-auto px-4 md:px-8">
        {/* Section Header */}
        <div
          className="text-center mb-16"
          style={{
            opacity: isVisible ? 1 : 0,
            transform: isVisible ? "translateY(0)" : "translateY(30px)",
            transition: "opacity 0.6s ease, transform 0.6s ease",
          }}
        >
          <span className="text-[#D4AF37] text-xs uppercase tracking-[5px] mb-4 block font-medium">
            Our Collection
          </span>
          <h2
            className="text-4xl md:text-5xl font-bold text-[#FAFAFA] mb-5"
            style={{ fontFamily: "'Playfair Display', serif" }}
            data-testid="products-heading"
          >
            Premium Makhana Grades
          </h2>
          <p className="text-[#A1A1AA] text-lg max-w-2xl mx-auto leading-relaxed">
            Each grade is measured in{" "}
            <span className="text-[#D4AF37] font-semibold">"Suta"</span> — a
            traditional Bihar unit where{" "}
            <span className="text-[#FAFAFA]">1 Suta = 3.17mm</span>. Higher
            the Suta, larger and rarer the Makhana.
          </p>
        </div>

        {/* Products Grid */}
        <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-4 gap-6">
          {products.map((product, i) => (
            <div
              key={product.id}
              className="group relative bg-[#121212] border border-white/5 hover:border-[#D4AF37]/40 rounded-2xl overflow-hidden transition-all duration-500 hover:shadow-[0_20px_60px_rgba(212,175,55,0.15)] hover:-translate-y-2 flex flex-col"
              style={{
                opacity: isVisible ? 1 : 0,
                transform: isVisible ? "translateY(0)" : "translateY(40px)",
                transition: `opacity 0.7s ease ${0.15 + i * 0.1}s, transform 0.7s ease ${0.15 + i * 0.1}s`,
              }}
              data-testid={`product-card-${product.id}`}
            >
              {/* Tag */}
              {product.tag && (
                <div
                  className={`absolute top-3 left-3 z-10 text-[10px] uppercase tracking-widest font-bold px-2.5 py-1 rounded-full ${product.tagClass}`}
                >
                  {product.tag}
                </div>
              )}

              {/* Product Image */}
              <div className="relative h-48 overflow-hidden">
                <img
                  src={MAKHANA_IMG}
                  alt={product.name}
                  className="w-full h-full object-cover group-hover:scale-110 transition-transform duration-700"
                />
                <div className="absolute inset-0 bg-gradient-to-t from-[#121212] via-[#121212]/20 to-transparent" />
                {/* Grade indicator overlay */}
                <div className="absolute bottom-3 right-3 bg-[#050505]/80 border border-[#D4AF37]/30 px-2 py-1 rounded-lg backdrop-blur-sm">
                  <span className="text-[#D4AF37] text-xs font-bold">{product.size}</span>
                </div>
              </div>

              {/* Product Content */}
              <div className="p-5 flex flex-col flex-1">
                {/* Badge */}
                <span
                  className={`inline-block text-[10px] font-semibold uppercase tracking-widest px-3 py-1 rounded-full border mb-3 w-fit ${product.badgeClass}`}
                >
                  {product.badge}
                </span>

                {/* Name */}
                <h3
                  className="text-[#FAFAFA] text-xl font-bold mb-1"
                  style={{ fontFamily: "'Playfair Display', serif" }}
                  data-testid={`product-name-${product.id}`}
                >
                  {product.name}
                </h3>
                <p
                  className="text-[#D4AF37]/80 text-xs mb-3"
                  style={{ fontFamily: "'Noto Sans Devanagari', sans-serif" }}
                >
                  {product.hindi}
                </p>

                {/* Stars */}
                <div className="flex gap-0.5 mb-3">
                  {Array.from({ length: 5 }).map((_, j) => (
                    <Star
                      key={j}
                      size={11}
                      className={
                        j < product.stars
                          ? "text-[#D4AF37] fill-[#D4AF37]"
                          : "text-[#333]"
                      }
                    />
                  ))}
                </div>

                {/* Description */}
                <p className="text-[#A1A1AA] text-sm leading-relaxed mb-4 flex-1">
                  {product.desc}
                </p>

                {/* Use Cases */}
                <div className="flex flex-wrap gap-1.5 mb-5">
                  {product.uses.map((use) => (
                    <span
                      key={use}
                      className="text-[10px] text-[#A1A1AA] border border-white/10 px-2 py-0.5 rounded-full"
                    >
                      {use}
                    </span>
                  ))}
                </div>

                {/* Price & CTA */}
                <div className="flex items-center justify-between mt-auto pt-3 border-t border-white/5">
                  {/* <span className="text-[#D4AF37] font-bold text-sm">{product.priceRange}</span> */}
                  <button
                    onClick={scrollToDistributor}
                    className="flex items-center gap-1 text-[#D4AF37] text-xs hover:text-[#F4E3B2] transition-colors group"
                    data-testid={`product-enquire-${product.id}`}
                  >
                    Enquire
                    <ArrowRight
                      size={12}
                      className="group-hover:translate-x-1 transition-transform duration-200"
                    />
                  </button>
                </div>
              </div>
            </div>
          ))}
        </div>

        {/* Grade Guide */}
        <div
          className="mt-12 p-6 md:p-8 bg-[#121212] border border-white/5 rounded-2xl"
          style={{
            opacity: isVisible ? 1 : 0,
            transition: "opacity 0.6s ease 0.6s",
          }}
          data-testid="grade-guide"
        >
          <p className="text-center text-[#A1A1AA] text-sm mb-6">
            <span className="text-[#D4AF37] font-semibold">Suta Grade Guide</span> — Understanding the Bihar Makhana Grading System
          </p>
          <div className="grid grid-cols-2 md:grid-cols-4 gap-4">
            {[
              { suta: "4 Suta", mm: "≈ 12.68mm", color: "text-blue-400" },
              { suta: "5 Suta", mm: "≈ 15.85mm", color: "text-[#D4AF37]" },
              { suta: "6 Suta", mm: "≈ 19.02mm", color: "text-amber-400" },
              { suta: "Mix", mm: "Multiple", color: "text-green-400" },
            ].map((g) => (
              <div key={g.suta} className="text-center p-3 rounded-xl bg-[#0A0A0A] border border-white/5">
                <p className={`font-bold text-lg ${g.color}`} style={{ fontFamily: "'Playfair Display', serif" }}>
                  {g.suta}
                </p>
                <p className="text-[#525252] text-xs">{g.mm}</p>
              </div>
            ))}
          </div>
        </div>

        {/* CTA */}
        <div
          className="text-center mt-10"
          style={{
            opacity: isVisible ? 1 : 0,
            transition: "opacity 0.6s ease 0.7s",
          }}
        >
          <button
            onClick={scrollToDistributor}
            className="bg-[#D4AF37] text-black px-10 py-4 rounded-full font-bold text-base hover:bg-[#F4E3B2] transition-all duration-300 shadow-[0_0_30px_rgba(212,175,55,0.3)] hover:shadow-[0_0_60px_rgba(212,175,55,0.5)] hover:scale-105"
            data-testid="products-bulk-btn"
          >
            Request Bulk Pricing
          </button>
          <p className="text-[#525252] text-sm mt-3">
            Min. order 50kg · All-inclusive pricing · Pan India delivery
          </p>
        </div>
      </div>
    </section>
  );
};

export default ProductsSection;
